import type {
  ApplicationInsights,
  IEventTelemetry,
  IExceptionTelemetry,
  ITraceTelemetry,
  SeverityLevel,
} from '@microsoft/applicationinsights-web'
import { useEffect } from 'react'

import { getEnvVar } from '../lib/env'

// SeverityLevel.Error without importing the SDK's runtime enum object, which
// would pull the whole package back into the initial bundle. The SDK types
// Error as 3 (eSeverityLevel.Error).
export const SEVERITY_LEVEL_ERROR: SeverityLevel = 3

let appInsightsInstance: ApplicationInsights | undefined

// Wrappers so consumers (ErrorBoundary) don't need the SDK loaded to compile
// or run: telemetry sent before the deferred chunk lands is dropped, which is
// the accepted cost of keeping 165 KB off the critical path.
export const trackTrace = (trace: ITraceTelemetry): void => {
  appInsightsInstance?.trackTrace(trace)
}

export const trackException = (exception: IExceptionTelemetry): void => {
  appInsightsInstance?.trackException(exception)
}

export const trackEvent = (
  event: IEventTelemetry,
  customProperties?: Record<string, string>
): void => {
  appInsightsInstance?.trackEvent(event, customProperties)
}

export function useAppInsights() {
  useEffect(() => {
    const initialize = async () => {
      try {
        // Deferred: @microsoft/applicationinsights-web does not need to gate
        // first paint. The dynamic import gives it its own chunk fetched after
        // mount; the initial page view is tracked once it lands. This also
        // stops loadAppInsights from re-running on every App render, which the
        // previous render-time call did.
        const { ApplicationInsights } = await import(
          '@microsoft/applicationinsights-web'
        )
        const instance = new ApplicationInsights({
          config: {
            connectionString: getEnvVar('REACT_APP_APPINSIGHT'),
            /* ...Other Configuration Options... */
          },
        })
        instance.loadAppInsights()
        instance.trackPageView() // Manually call trackPageView to establish the current user/session/pageview
        appInsightsInstance = instance
      } catch {
        // Telemetry is best-effort: a failed SDK load must never break the
        // app, and there is no telemetry channel to report it to.
      }
    }
    // initialize never rejects — its body is wrapped in a catch-all.
    initialize()
  }, [])
}
